import { useTranslation } from "@warpme/i18n"
import { type FC, useCallback, useMemo, useState } from "react"

import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
  DrawerTrigger,
} from "@/components/ui/drawer"
import { Icon } from "@/components/ui/icon"
import { getCurrentLanguage, SupportedLanguage } from "@/lib/current-language"
import { cn } from "@/lib/utils"

const LANGUAGES = [
  { code: SupportedLanguage.Ja, i18nCode: SupportedLanguage.Ja, label: "日本語" },
  { code: SupportedLanguage.En, i18nCode: SupportedLanguage.En, label: "English" },
  { code: SupportedLanguage.Ko, i18nCode: SupportedLanguage.Ko, label: "한국어" },
  { code: SupportedLanguage.ZhHans, i18nCode: SupportedLanguage.ZhHans, label: "简体中文" },
  { code: SupportedLanguage.ZhHant, i18nCode: SupportedLanguage.ZhHant, label: "繁體中文" },
] as const
type Language = (typeof LANGUAGES)[number]

export const LanguageSelector: FC = () => {
  const { i18n } = useTranslation("consumer")
  const [open, setOpen] = useState(false)
  const selectedLanguageCode = useMemo(() => {
    return getCurrentLanguage(i18n)
  }, [i18n.language, i18n.resolvedLanguage])

  const handleSelect = useCallback(
    (_code: Language["code"], i18nCode: Language["i18nCode"]) => {
      i18n.changeLanguage(i18nCode)
      setOpen(false)
    },
    [i18n]
  )

  return (
    <Drawer direction="right" open={open} onOpenChange={setOpen}>
      <DrawerTrigger asChild>
        <button type="button" className="text-[11px] font-semibold leading-[1.35] text-[#737373]">
          Language
        </button>
      </DrawerTrigger>
      <DrawerContent className="w-[calc(100%-60px)] max-w-none gap-0 border-l border-border bg-bg-white p-0 shadow-right">
        <DrawerHeader className="relative h-12 space-y-0 px-4 pt-3 pb-0 text-left">
          <DrawerTitle className="flex h-9 items-center justify-center text-title-sm font-semibold text-text">
            Select Language
          </DrawerTitle>
          <DrawerClose asChild>
            <button
              type="button"
              className="absolute top-0 right-[6px] flex size-9 items-center justify-center rounded-full bg-highlight backdrop-blur-[10px]"
            >
              <Icon name="close" className="size-6 text-text" />
            </button>
          </DrawerClose>
        </DrawerHeader>

        <div className="flex-1 overflow-y-auto">
          {LANGUAGES.map((lang) => (
            <button
              key={lang.code}
              type="button"
              onClick={() => handleSelect(lang.code, lang.i18nCode)}
              className={cn(
                "h-11 w-full border-b border-border px-4 text-left text-body-md font-semibold text-text",
                selectedLanguageCode === lang.code && "bg-bg-primary text-primary"
              )}
            >
              {lang.label}
            </button>
          ))}
        </div>
      </DrawerContent>
    </Drawer>
  )
}
